import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface BaseReportProps {
  title: string;
  children?: React.ReactNode;
  onGenerateReport?: (filters: Record<string, any>) => void;
}

const BaseReport: React.FC<BaseReportProps> = ({ title, children, onGenerateReport }) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [bank, setBank] = useState<string>('');
  const [borrower, setBorrower] = useState<string>('');
  const [loanType, setLoanType] = useState<string>('');
  const [grouping, setGrouping] = useState<string>('daily');
  const [outputFormat, setOutputFormat] = useState<string>('screen');

  const handleGenerateReport = () => {
    const filters = {
      startDate,
      endDate,
      bank,
      borrower,
      loanType,
      grouping,
      outputFormat,
    };
    if (onGenerateReport) {
      onGenerateReport(filters);
    } else {
      console.log('Generating report with filters:', filters);
    }
  };

  const handleSaveConfig = () => {
    // TODO: Implement save configuration logic
    console.log('Saving report configuration');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {title}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Bank</InputLabel>
              <Select
                value={bank}
                label="Bank"
                onChange={(e: SelectChangeEvent) => setBank(e.target.value)}
              >
                <MenuItem value="">All Banks</MenuItem>
                {/* Add bank options here */}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Borrower</InputLabel>
              <Select
                value={borrower}
                label="Borrower"
                onChange={(e: SelectChangeEvent) => setBorrower(e.target.value)}
              >
                <MenuItem value="">All Borrowers</MenuItem>
                {/* Add borrower options here */}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Loan Type</InputLabel>
              <Select
                value={loanType}
                label="Loan Type"
                onChange={(e: SelectChangeEvent) => setLoanType(e.target.value)}
              >
                <MenuItem value="">All Loan Types</MenuItem>
                {/* Add loan type options here */}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Grouping</InputLabel>
              <Select
                value={grouping}
                label="Grouping"
                onChange={(e: SelectChangeEvent) => setGrouping(e.target.value)}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="half-yearly">Half-yearly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Output Format</InputLabel>
              <Select
                value={outputFormat}
                label="Output Format"
                onChange={(e: SelectChangeEvent) => setOutputFormat(e.target.value)}
              >
                <MenuItem value="screen">Screen</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
                <MenuItem value="excel">Excel</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateReport}
          >
            Generate Report
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleSaveConfig}
          >
            Save Configuration
          </Button>
        </Box>
      </Paper>

      {children}
    </Box>
  );
};

export default BaseReport; 